import React, {useState, useEffect, useRef} from 'react';
import {
    IonList,
    IonItem,
    IonLabel,
    IonSearchbar,
    IonSpinner,
    IonNote,
    IonButton,
    IonModal,
    IonHeader, IonToolbar, IonButtons, IonTitle, IonContent
} from '@ionic/react';
import { useQuery } from '@tanstack/react-query';
import type {Item} from "./AppTypeaheadTypes";

interface AutocompleteProps {
    /**
     * An optional label to display above the search bar.
     */
    label?: string;

    /**
     * Called when a user picks a suggestion or enters a custom input.
     */
    onSelect: (value: Item) => void;

    /**
     * Minimum number of characters before we attempt to fetch server data.
     * Defaults to 2.
     */
    minChars?: number;

    /**
     * Debounce delay (milliseconds) for the search input.
     * Defaults to 300 ms.
     */
    debounceDelay?: number;

    /**
     * Placeholder for the search bar.
     */
    placeholder?: string;

    /**
     * Existing value when controlled from outside (optional).
     */
    value?: Item;

    /**
     * Whether the input is disabled.
     */
    disabled: boolean;

    /**
     * Autocomplete data url
     */
    fetchItems?: (query: string) => Promise<Item[]>;
}

export const Autocomplete: React.FC<AutocompleteProps> = ({
                                                              label,
                                                              onSelect,
                                                              minChars = 1,
                                                              debounceDelay = 300,
                                                              placeholder = 'Type to search...',
                                                              value: externalValue,
                                                              disabled = false,
                                                              fetchItems = () => Promise.resolve([]),
                                                          }) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const modal = useRef<any>();
    const [filteredItems, setFilteredItems] = useState<Item[]>([]);

    const triggerId = "open-autocomplete-modal-" + label!.replace(' ', '-');


    const {
        data: suggestions = [],
        isLoading,
        isError,
        refetch
    } = useQuery(
        {
            queryKey: ['autocomplete', triggerId],
            queryFn: () => fetchItems(searchTerm),
        },
    );

    /**
     * Update the rendered view with
     * the provided search query. If no
     * query is provided, all data
     * will be rendered.
     */
    const filterList = (searchQuery: string | null | undefined) => {
        /**
         * If no search query is defined,
         * return all options.
         */
        if (searchQuery === undefined || searchQuery === null || searchQuery === '') {
            setFilteredItems([...suggestions]);
        } else {
            /**
             * Otherwise, normalize the search
             * query and check to see which items
             * contain the search query as a substring.
             */
            const normalizedQuery = searchQuery.toLowerCase();
            setFilteredItems(
                suggestions.filter((item) => {
                    return item.title.toLowerCase().includes(normalizedQuery);
                })
            );
        }
    };

    useEffect(() => {
        filterList(searchTerm);
    }, [searchTerm, debounceDelay]);

    const handleSelect = (value: Item) => {
        setSearchTerm(value.id);
        onSelect(value);

        modal.current?.dismiss();
    };

    const cancelChanges = () => {
        modal.current?.dismiss();
    };

    return (
        <div style={{ margin: '1rem' }}>
            <IonItem>
                <IonLabel>{ label }</IonLabel>
                <IonButton id={triggerId} disabled={disabled} style={{textTransform: 'none'}}>
                    { externalValue?.title ?? 'Select an option ...' }
                </IonButton>
            </IonItem>

            <IonModal ref={modal} trigger={triggerId} onIonModalWillPresent={() => refetch()}>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton onClick={cancelChanges}>Cancel</IonButton>
                        </IonButtons>
                        <IonTitle>{label}</IonTitle>
                    </IonToolbar>
                    <IonToolbar>
                        <IonSearchbar
                            placeholder={placeholder}
                            value={searchTerm}
                            onIonInput={(e) => setSearchTerm(e.detail.value!)}
                            showClearButton="always"
                            debounce={300}
                        />
                    </IonToolbar>
                </IonHeader>

                <IonContent color="light" class="ion-padding">
                    <IonList>
                        {searchTerm.length >= minChars && (
                            <IonItem button onClick={() => handleSelect({id: searchTerm, title: searchTerm, description: ''})}>
                                <IonLabel>
                                    Use "<strong>{searchTerm}</strong>" (custom)
                                </IonLabel>
                                <IonNote slot="end">(No suggestions found)</IonNote>
                            </IonItem>
                        )}

                        {isLoading && (
                            <IonItem>
                                <IonSpinner slot="start" />
                                <IonLabel>Loading suggestions...</IonLabel>
                            </IonItem>
                        )}

                        {isError && (
                            <IonItem color="danger">
                                <IonLabel>Error loading suggestions</IonLabel>
                            </IonItem>
                        )}

                        {filteredItems.length > 0 && (
                            <>
                                {filteredItems.map(suggestion => (
                                    <IonItem key={suggestion.id} button onClick={() => handleSelect(suggestion)}>
                                        <IonLabel>
                                            <h3>{ suggestion.title }</h3>
                                            <p>{ suggestion.description } - { suggestion.id }</p>
                                        </IonLabel>
                                    </IonItem>
                                ))}
                            </>
                        )}
                    </IonList>

                    {/*<IonList id="modal-list" inset={true}>*/}
                    {/*    {filteredItems.map((item) => (*/}
                    {/*        <IonItem key={item.id} button onClick={() => confirmChanges(item)}>*/}
                    {/*            <IonLabel>*/}
                    {/*                <h3>{ item.title }</h3>*/}
                    {/*                <p>{ item.description } - { item.id }</p>*/}
                    {/*            </IonLabel>*/}
                    {/*        </IonItem>*/}
                    {/*    ))}*/}
                    {/*</IonList>*/}
                </IonContent>
            </IonModal>
        </div>
    );
};